import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import Logo from '../components/layout/Logo';

const DeleteAccountPage: React.FC = () => {
  const { user } = useAuthStore();
  const [email, setEmail] = useState(user?.email ?? '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account-request`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ email: email.trim(), reason: reason.trim() }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Request failed');

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-[#3A3C3E] py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo className="h-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-white">Delete your account</h2>
        <p className="mt-2 text-center text-sm text-gray-300">
          This is permanent and cannot be undone.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-md rounded-xl sm:px-10">
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="text-4xl">✅</div>
              <h3 className="text-lg font-semibold text-gray-900">Request received</h3>
              <p className="text-sm text-gray-600">
                We've received your deletion request for <strong>{email}</strong> and sent you a confirmation email.
              </p>
              <p className="text-sm text-gray-600">
                Your account and all associated data will be permanently deleted within <strong>30 days</strong>.
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Changed your mind? Email us at{' '}
                <a href="mailto:support@hustli.app" className="text-primary underline">
                  support@hustli.app
                </a>{' '}
                as soon as possible.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 font-medium mb-1">This will permanently delete:</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                  <li>Your account and profile</li>
                  <li>All teams you own and their rosters</li>
                  <li>All practices, drills, and game plans</li>
                  <li>All player cards and development notes</li>
                  <li>Your subscription (no refunds for unused time)</li>
                </ul>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 text-sm">
                  {error}
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Your email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                    Reason for leaving{' '}
                    <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 resize-none text-sm"
                    placeholder="Help us improve by sharing why you're leaving…"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-stone-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">
                    I understand that deleting my account is <strong>permanent and irreversible</strong> and that all my data will be erased.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={!confirmed || loading}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-xl text-base font-bold hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting request…' : 'Submit deletion request'}
                </button>
              </form>

              <div className="mt-5 text-center">
                <Link to="/settings" className="text-sm text-gray-500 hover:text-gray-700 underline">
                  Cancel — go back to settings
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
